import { getWPModuleSpacing } from "@app/api/wp-page-fetcher/utils/get-wp-module-spacing";
import { IModuleFetcher } from "@app/api/wp-page-fetcher/module-selector/module-selector";
import Logger from "@app/util/logger";
import { WordPressPostModule } from "@app/api/modules/wordpress-module/wordpress-module";
import { ProductSingleModule } from "@app/api/modules/product-single/product-single.module";
import { checkSpecialSinglePage } from "@app/api/wp-page-fetcher/utils/check-special-single-page";
import { moduleMapperThrowMessage } from "@app/api/wp-page-fetcher/utils/module-mapper-error-handling";
import { getLastArrayItem } from "@app/util/get-last-array-item";
import { singleProductGetApi } from "@app/middleware/api/single-product-get.api";
import { OutProductExtendedViewDTO } from "@app/bf-api";
import { setSelectedProduct } from "@app/redux/reducers/product";
import { GET_CONFIG } from "@app/util";
import { findPlatformContent } from "@app/api/wp-page-fetcher/utils/platform-content-array";

import { IWPProductSingleModuleFull } from "./product-single-module.interface";
import { breadcrumbComponentMapper } from "../../component-mappers";

export interface IProductSingleMapper extends IModuleFetcher {
  module: IWPProductSingleModuleFull;
}

export const productSingleModuleMapper = async (
  props: IProductSingleMapper
): Promise<ProductSingleModule | WordPressPostModule | undefined> => {
  try {
    const {
      module,
      module: { data },
      platformId,
      productPageUrlName,
      splittedUrl,
      pageData,
      pageUrl
    } = props;

    if (!data || !checkSpecialSinglePage(productPageUrlName, splittedUrl))
      return moduleMapperThrowMessage("No data found in productSingleModuleMapper");

    const product = await setSingleProductInStore(props);

    if (product === 302) {
      return { id: "30", name: "ProductSingleModule", status: 302 };
    }

    if (!product) return moduleMapperThrowMessage("Cannot get single product in productSingleModuleMapper");

    const breadcrumbProps = await breadcrumbComponentMapper({
      platformId,
      pageUrl,
      variant: "dark",
      title: pageData.meta?.breadcrumbTitle
    });

    return {
      id: "30",
      name: "ProductSingleModule",
      ...getWPModuleSpacing(module.data, module.setMargins),
      background: { backgroundColour: data.bgColor },
      product: {
        ...product,
        seo: {
          ...product.seo,
          jsonSchema: product.seo?.jsonSchema || getStructuredData(product, platformId, pageUrl)
        }
      },
      compareTitle: data.compareTitle,
      noResultText: data.noResultText,
      breadcrumbProps
    };
  } catch (e) {
    Logger.logError(e, "productSingleModuleMapper went wrong");

    return undefined;
  }
};

export const setSingleProductInStore = async (
  props: IProductSingleMapper
): Promise<OutProductExtendedViewDTO | undefined | 302> => {
  const { splittedUrl, platformId, res, dispatch } = props;

  if (!splittedUrl) return undefined;

  try {
    const slug = getLastArrayItem(splittedUrl);
    const product = await singleProductGetApi(slug, platformId);

    if (!product) return moduleMapperThrowMessage("Cannot get single product");

    if (product === 302) return 302;

    if (res) {
      res.locals.store.dispatch(setSelectedProduct(product));
    }
    if (dispatch) {
      dispatch(setSelectedProduct(product));
    }

    return product;
  } catch (e) {
    Logger.logError(e, "Error in: getSingleProduct");

    return undefined;
  }
};

const getStructuredData = (product: OutProductExtendedViewDTO, platformId: number, pageUrl: string): string => {
  const url = `${GET_CONFIG.CLIENT_URL.slice(0, -1)}${pageUrl}`;

  const platform = findPlatformContent(platformId);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.title,
    image: product.image?.url,
    description: product.seo?.pageText,
    brand: {
      "@type": "Brand",
      name: product?.deals?.[0]?.brands?.[0]?.name
    },
    sku: product.ean?.split("|")?.[0],
    offers: {
      "@type": "AggregateOffer",
      url,
      priceCurrency: platform.currency,
      lowPrice: product?.deals?.[0]?.price,
      highPrice: product?.deals?.at(-1)?.price,
      offerCount: product?.deals?.length
    }
  };

  return JSON.stringify(structuredData);
};
